import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { API_BASE_URL } from '../../config/api';
import Pagination from '../../components/Pagination/Pagination';
import './AllCategoriesPage.css';

const AllCategoriesPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const categoriesPerPage = 24; // 4 cards per row × 6 rows = 24

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleCategoryClick = (category) => {
    navigate(`/products?category=${category.id}`);
  };

  const handleSubcategoryClick = (category, subcategory) => {
    navigate(`/products?category=${category.id}&subcategory=${subcategory.slug}`);
  };

  // Default image for categories without images
  const defaultImage = 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop';

  // Handle image error - show default image on error
  const handleImageError = (e) => {
    e.target.onerror = null; // Prevent infinite loop
    e.target.src = defaultImage;
  };

  // Pagination logic
  const totalPages = Math.ceil(categories.length / categoriesPerPage);
  const indexOfLastCategory = currentPage * categoriesPerPage;
  const indexOfFirstCategory = indexOfLastCategory - categoriesPerPage;
  const currentCategories = categories.slice(indexOfFirstCategory, indexOfLastCategory);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="all-categories-page">
      <Header />
      
      <main className="categories-main-content">
        <div className="categories-container">
          {/* Breadcrumb */}
          <div className="categories-breadcrumb">
            <Link to="/" className="breadcrumb-link">Home</Link>
            <svg className="breadcrumb-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="breadcrumb-current">All Categories</span>
          </div>

          {/* Page Header */}
          <div className="categories-header">
            <h1 className="categories-title">All Categories</h1>
            <p className="categories-subtitle">Find your desired category and explore products and services.</p>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="loading-container">
              <p>Loading categories...</p>
            </div>
          ) : (
            <>
              {/* Categories Grid */}
              <div className="categories-grid-section">
                <div className="categories-grid">
                  {currentCategories.map((category) => (
                    <div key={category.id} className="category-card" onClick={() => handleCategoryClick(category)}>
                      <div className="category-image-wrapper">
                        <img 
                          src={category.image || defaultImage} 
                          alt={category.name}
                          className="category-image"
                          onError={handleImageError}
                        />
                      </div>
                      <div className="category-info">
                        <h3 className="category-name">{category.name}</h3>
                        <div className="subcategories-list">
                          {category.subcategories && category.subcategories.slice(0, 4).map((subcategory) => (
                            <div 
                              key={subcategory.id || subcategory.name} 
                              className="subcategory-item"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSubcategoryClick(category, subcategory);
                              }}
                            >
                              <span className="subcategory-name">{subcategory.name || subcategory}</span>
                            </div>
                          ))}
                          <span 
                            className="view-all-link"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCategoryClick(category);
                            }}
                          >
                            View All ....
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pagination */}
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AllCategoriesPage;
